import { CURRENT_CLIENT, toTitleCase } from "@/constants"
import { PLATFORM_PATH_MODULES, isPlatformModuleEnabled } from "@/lib/platform-modules"
import { getSignupMode, accessRequestEnabled, getAccessRequestConfig } from "@/constants/signup"
import { getPluginSlots } from "@/plugin/slots"
import type { SidebarNavFocusConfig, SidebarNavFocusKey, SidebarNavItemConfig } from "@/types/sidebar-nav"

export interface ResolvedSidebarNavItem {
  path: string
  icon: SidebarNavItemConfig["icon"]
  label: string
  description?: string
  exact: boolean
}

interface ResolveSidebarNavArgs {
  focus: SidebarNavFocusKey | null | undefined
  availableFoci?: string[]
}

/** Core focuses only; domains and packs add theirs with `nav.add`. */
const DEFAULT_SIDEBAR_NAV: SidebarNavFocusConfig = {
  platform: [
    { path: "/platform", icon: "Home", exact: true, label: "Home" },
    { path: "/platform/engagement", icon: "BarChart3", label: "Engagement" },
    { path: "/platform/evaluation-rubrics", icon: "ClipboardList", label: "Rubrics" },
    { path: "/platform/simulation", icon: "FlaskConical", label: "Simulation" },
    { path: "/platform/classifiers", icon: "Sparkles", label: "Classifiers" },
    { path: "/platform/feedback", icon: "Megaphone", requiresFeature: "productFeedback", label: "Feedback" },
    { path: "/platform/notices", icon: "Bell", label: "Notices" },
    { path: "/platform/users", icon: "Users" },
    { path: "/platform/waitlist", icon: "UserPlus", label: "Waitlist", requiresSignupGating: true },
    { path: "/platform/organisations", icon: "Building2", label: "Organisations" },
  ],
  developer: [
    { path: "/developer", icon: "Home", exact: true, label: "Home" },
    { path: "/developer/agent-evaluations", icon: "Activity", label: "Agent evaluations" },
  ],
}

/** Append plugin items, honouring `after` anchors. */
const PLATFORM_NOTICES_ITEM: SidebarNavItemConfig = {
  path: "/platform/notices",
  icon: "Bell",
  label: "Notices",
}

/** Packs that replace the platform list still get the shared notices row, beside Users. */
export function ensurePlatformNotices(items: SidebarNavItemConfig[]): SidebarNavItemConfig[] {
  if (items.some((item) => item.path === PLATFORM_NOTICES_ITEM.path)) return items
  const usersAt = items.findIndex((item) => item.path === "/platform/users")
  const next = [...items]
  if (usersAt === -1) {
    next.push(PLATFORM_NOTICES_ITEM)
    return next
  }
  next.splice(usersAt, 0, PLATFORM_NOTICES_ITEM)
  return next
}

export function mergeNavItems(
  base: SidebarNavItemConfig[],
  added: SidebarNavItemConfig[],
): SidebarNavItemConfig[] {
  const merged = [...base]
  for (const item of added) {
    const anchor = item.after ? merged.findIndex((existing) => existing.path === item.after) : -1
    if (anchor === -1) {
      merged.push(item)
    } else {
      let insertAt = anchor + 1
      while (insertAt < merged.length && merged[insertAt].after === item.after) insertAt += 1
      merged.splice(insertAt, 0, item)
    }
  }
  return merged
}

function getRouteCopy(path: string) {
  return (CURRENT_CLIENT as any).routes?.[path] as { label?: string; description?: string } | undefined
}

function isFeatureEnabled(feature?: string): boolean {
  if (!feature) return true
  const value = (CURRENT_CLIENT as any).features?.[feature]
  // Supports both shapes: `"health": true` and `"productFeedback": { "enabled": true }`.
  if (value && typeof value === 'object') {
    return value.enabled === true
  }
  return value === true
}

function shouldIncludeItem(item: SidebarNavItemConfig, availableFoci?: string[]) {
  if (!isFeatureEnabled(item.requiresFeature)) return false
  if (item.requiresSignupGating) {
    if (!accessRequestEnabled() && getSignupMode() !== "approval") return false
  }
  if (item.requiresAvailableFocus && !availableFoci?.includes(item.requiresAvailableFocus)) return false
  const platformModule = PLATFORM_PATH_MODULES[item.path]
  if (platformModule && !isPlatformModuleEnabled(platformModule)) return false
  return true
}

function labelFromPathPath(pathLike: string): string {
  const trimmed = pathLike.replace(/^\/+|\/+$/g, '')
  if (!trimmed) return pathLike
  const segment = trimmed.split('/').pop() ?? trimmed
  return toTitleCase(segment.replace(/-/g, ' '))
}

function resolveItemLabel(item: SidebarNavItemConfig, fallbackLabel: string) {
  const routeCopy = getRouteCopy(item.path)
  const rawFallback = item.label ?? routeCopy?.label ?? fallbackLabel
  if (typeof rawFallback === 'string' && rawFallback.startsWith('/')) {
    return labelFromPathPath(rawFallback)
  }
  return toTitleCase(rawFallback)
}

function resolveItemDescription(item: SidebarNavItemConfig, fallbackDescription?: string) {
  const routeCopy = getRouteCopy(item.path)
  return item.description ?? routeCopy?.description ?? fallbackDescription
}

export function resolveSidebarNav({
  focus,
  availableFoci = [],
}: ResolveSidebarNavArgs): ResolvedSidebarNavItem[] {
  if (!focus) return []

  const clientNav = ((CURRENT_CLIENT as any).sidebarNav ?? {}) as SidebarNavFocusConfig
  const configuredItems =
    clientNav[focus] ??
    mergeNavItems(DEFAULT_SIDEBAR_NAV[focus] ?? [], getPluginSlots().navItems[focus] ?? [])

  const withNotices = focus === "platform" ? ensurePlatformNotices(configuredItems) : configuredItems
  const items = withNotices.filter((item) => shouldIncludeItem(item, availableFoci))

  return items.map((item) => ({
    path: item.path,
    icon: item.icon,
    label:
      item.path === '/platform/waitlist' && getAccessRequestConfig().adminLabel
        ? getAccessRequestConfig().adminLabel!
        : resolveItemLabel(item, item.path),
    description: resolveItemDescription(item),
    exact: item.exact ?? item.path === `/${focus}`,
  }))
}
